/**
 * Composer Paste-Image Handler — pure helper for the Ask Argus composer.
 *
 * Wave 2C (chat-ui-improvements).
 *
 * ## What this does
 * Extracts `image/*` blobs from a `ClipboardEvent` and separates them from
 * any text content in the same paste event. The caller receives:
 *   - `files`         — array of `File` objects for each image blob found.
 *   - `remainingText` — any text from `clipboardData.getData('text/plain')`
 *                       that was in the clipboard alongside the image(s).
 *                       Usually empty when an image is pasted (OS typically
 *                       only places an image OR text on the clipboard, not both).
 *
 * The function is intentionally **pure** — no React, no side-effects. It
 * returns the extracted data; the caller decides what to do with it (open the
 * upload dialog, show a toast, etc.).
 *
 * ## Spec
 * CHAT-UI-IMPROVEMENT-DESIGN.md §3.4 — Paste image handler.
 *
 * ## Note on backend status
 * The file attachment pipeline is not yet wired (audit §7 limitation 8). v1
 * captures the file client-side and the caller surfaces a "Will attach when
 * backend ships" toast. Image preview still works in the composer's upload
 * queue.
 */

// ── Types ────────────────────────────────────────────────────────────────────

export interface PasteImageResult {
  /**
   * Image files extracted from the clipboard event. Empty array when no
   * `image/*` items are present.
   */
  files: File[];
  /**
   * Text content (if any) present alongside the image(s). The caller can
   * decide whether to insert this into the textarea or discard it when
   * images are present.
   */
  remainingText: string;
}

// ── Pure helper ──────────────────────────────────────────────────────────────

/**
 * Extract image blobs from a paste clipboard event.
 *
 * @returns `{ files, remainingText }` — `files` is non-empty when at least
 *   one `image/*` item was found in the clipboard data.
 *
 * @example
 * ```ts
 * textarea.addEventListener('paste', (e) => {
 *   const { files, remainingText } = extractImagesFromClipboard(e);
 *   if (files.length > 0) {
 *     e.preventDefault();  // caller must call this to suppress the default paste
 *     openUploadQueue(files);
 *   }
 * });
 * ```
 */
export function extractImagesFromClipboard(e: ClipboardEvent): PasteImageResult {
  const clipboardData = e.clipboardData;
  if (!clipboardData) {
    return { files: [], remainingText: '' };
  }

  const files: File[] = [];
  const items = Array.from(clipboardData.items);

  for (const item of items) {
    if (item.kind === 'file' && item.type.startsWith('image/')) {
      const blob = item.getAsFile();
      if (blob) {
        // Normalize the file name: clipboard items often have an empty or
        // generic name. We give it a timestamped default so the upload queue
        // can display something meaningful.
        const ext = item.type.split('/')[1] ?? 'png';
        const name = blob.name && blob.name !== 'image' ? blob.name : `pasted-image-${Date.now()}.${ext}`;
        const namedFile =
          blob.name === name
            ? blob
            : new File([blob], name, { type: blob.type, lastModified: blob.lastModified });
        files.push(namedFile);
      }
    }
  }

  // Extract text portion — only meaningful when there are no images or the
  // caller wants to preserve mixed clipboard content.
  const remainingText = files.length > 0
    ? '' // suppress text portion when images found (typical OS behavior)
    : (clipboardData.getData('text/plain') ?? '');

  return { files, remainingText };
}

// ── React onPaste handler factory ────────────────────────────────────────────

/**
 * Factory that produces a React `onPaste` event handler wired to the
 * file-upload queue.
 *
 * @param onFilesExtracted — callback invoked when image blobs are found.
 *   Receives the array of `File` objects. The caller should open the upload
 *   dialog / add to queue.
 *
 * @returns A `React.ClipboardEventHandler<HTMLTextAreaElement>` ready to
 *   attach to `<Textarea onPaste={...}>`.
 *
 * @example
 * ```tsx
 * const handlePaste = makeComposerPasteHandler((files) => {
 *   setAttachOpen(true);
 *   pendingPastedFilesRef.current = files;
 * });
 * // …
 * <Textarea onPaste={handlePaste} />
 * ```
 */
export function makeComposerPasteHandler(
  onFilesExtracted: (files: File[]) => void,
): (e: React.ClipboardEvent<HTMLTextAreaElement>) => void {
  return (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const { files } = extractImagesFromClipboard(e.nativeEvent);
    if (files.length > 0) {
      e.preventDefault(); // suppress the default text paste
      onFilesExtracted(files);
    }
    // If no images found, let the default paste happen (text goes into textarea).
  };
}

// Import the React namespace for the ClipboardEvent type used in the
// return type of makeComposerPasteHandler.
import type React from 'react';
